import { pagesPath, staticPath } from "faqSrc/lib/$path";
import Link from "next/link";
import React, { FC } from "react";
import styled from "styled-components";
import Image from "components/atoms/Image";
import { breakPoint } from "lib/constants";
import CreatorSmallSizeSocialLinks from "~/components/modules/creator/SmallSizeSocialLinks";
import { convertToLinkHref, externalLinkAttrs } from "~/lib/utils/urlHelper";
import { User } from "~/models/user";

interface Props {
  host?: string;
  user: User;
}

const UserProfileWithDescription: FC<Props> = ({ host, user }) => {
  if (!user.id) {
    return <></>;
  }
  const userUrl = convertToLinkHref({
    host,
    url: pagesPath.creators._id(user.id).$url(),
  });

  return (
    <ProfileSummary>
      <Link href={userUrl} passHref>
        <ProfileUrl aria-label={user.name} {...externalLinkAttrs(userUrl)} />
      </Link>
      <ImageWrap>
        {user.imageUrl && (
          <Image
            alt={user.name}
            ampLayout="responsive"
            height={72}
            layout="responsive"
            src={user.imageUrl}
            width={72}
          />
        )}
      </ImageWrap>
      <Information>
        <UsernameWrapper>
          <JobTitle className="hide-scrollbar">
            {user.jobTitle ? user.jobTitle : "ゲームライター"}
          </JobTitle>
          <Name>{user.name}</Name>
          <ArrowWrapper>
            <Image
              alt="icon help"
              height="14"
              src={staticPath.img.icon_light_right_arrow_svg}
              width="8"
            />
          </ArrowWrapper>
        </UsernameWrapper>
        <Description className="limited-line--2">
          {user.description}
        </Description>
        <CreatorSocialLinksWrap>
          <CreatorSmallSizeSocialLinks user={user} />
        </CreatorSocialLinksWrap>
      </Information>
    </ProfileSummary>
  );
};

const ImageWrap = styled.div`
  border-radius: 50%;
  display: block;
  height: 72px;
  margin: auto;
  overflow: hidden;
  width: 72px;
  &:hover {
    opacity: 0.8;
  }
  @media only screen and (max-width: ${breakPoint["md"]}) {
    align-self: center;
  }
`;

const Information = styled.div`
  overflow: hidden;
  width: 100%;
`;

const UsernameWrapper = styled.div`
  color: var(--title-clr);
  display: flex;
  font-size: 16px;
  margin: 0 0 4px 6px;
  width: 100%;
`;

const JobTitle = styled.div`
  -ms-overflow-style: none;
  font-weight: 700;
  overflow-x: scroll;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const ArrowWrapper = styled.div`
  margin-left: 12px;
`;

const Name = styled.div`
  color: var(--title-clr);
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 400;
  margin-left: 8px;
`;

const Description = styled.div`
  color: var(--cap-clr);
  font-size: 14px;
  line-height: 26px;
  margin: 0 0 4px 6px;
`;

const ProfileSummary = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 72px auto;
  padding: 16px 0;
  position: relative;

  &:hover,
  &:active {
    background-color: var(--hv-box-bg-clr);
  }
  &:hover ${JobTitle}, &:active ${JobTitle}, &:hover ${Name}, &:active ${Name} {
    text-decoration: underline;
  }
`;

// In order to avoid nest <a> problem - hover and sns links -, we use position and z-index
// referring the code in https://price-rank.com/p/1/s/20
const CreatorSocialLinksWrap = styled.div`
  position: relative;
  z-index: 3;
`;

const ProfileUrl = styled.a`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
`;

export default UserProfileWithDescription;
